import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Plus, Trash2, Copy, Loader2, X, Save, Edit } from 'lucide-react';
import { promoCodesAPI } from '../../services/api';

const AdminPromoCodes = () => {
  const [promoCodes, setPromoCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: 10,
    min_purchase: 0,
    max_uses: 100,
    valid_from: new Date().toISOString().split('T')[0],
    valid_until: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  });

  useEffect(() => {
    let isMounted = true;
    
    const fetchPromoCodes = async () => {
      try {
        const response = await promoCodesAPI.getAll();
        if (isMounted) {
          setPromoCodes(response.data || []);
        }
      } catch (error) {
        if (isMounted && error.name !== 'CanceledError') {
          console.error('Error fetching promo codes:', error);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    
    fetchPromoCodes();
    
    return () => {
      isMounted = false;
    };
  }, []);

  const handleOpenModal = (promo = null) => {
    if (promo) {
      setEditingPromo(promo);
      setFormData({
        code: promo.code || '',
        discount_type: promo.discount_type || 'percentage',
        discount_value: promo.discount_value || 10,
        min_purchase: promo.min_purchase || 0,
        max_uses: promo.max_uses || 100,
        valid_from: promo.valid_from ? new Date(promo.valid_from).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        valid_until: promo.valid_until ? new Date(promo.valid_until).toISOString().split('T')[0] : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
      });
    } else {
      setEditingPromo(null);
      setFormData({
        code: '',
        discount_type: 'percentage',
        discount_value: 10,
        min_purchase: 0,
        max_uses: 100,
        valid_from: new Date().toISOString().split('T')[0],
        valid_until: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingPromo(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        valid_from: new Date(formData.valid_from).toISOString(),
        valid_until: new Date(formData.valid_until).toISOString()
      };

      if (editingPromo) {
        const response = await promoCodesAPI.update(editingPromo._id, submitData);
        setPromoCodes(promoCodes.map(p => p._id === editingPromo._id ? response.data : p));
        alert('Promo code updated!');
      } else {
        const response = await promoCodesAPI.create(submitData);
        setPromoCodes([response.data, ...promoCodes]);
        alert('Promo code created!');
      }
      handleCloseModal();
    } catch (error) {
      console.error('Error saving promo code:', error);
      alert(error.response?.data?.detail || 'Failed to save promo code');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this promo code?')) {
      try {
        await promoCodesAPI.delete(id);
        setPromoCodes(promoCodes.filter(p => p._id !== id));
        alert('Promo code deleted!');
      } catch (error) {
        console.error('Error deleting promo code:', error);
        alert('Failed to delete promo code');
      }
    }
  };

  const handleToggle = async (id) => {
    try {
      const response = await promoCodesAPI.toggle(id);
      setPromoCodes(promoCodes.map(p => 
        p._id === id ? { ...p, is_active: response.data.is_active } : p
      ));
    } catch (error) {
      console.error('Error toggling promo code:', error);
      alert('Failed to toggle promo code');
    }
  };

  const copyToClipboard = (code) => {
    navigator.clipboard.writeText(code);
    alert('Code copied to clipboard!');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-accent" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-charcoal font-heading">Promo Codes</h1>
            <p className="text-stone-grey font-body">{promoCodes.length} total codes</p>
          </div>
          <Button 
            className="bg-muted-green hover:bg-muted-green/90 text-white font-body"
            onClick={() => handleOpenModal()}
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Promo Code
          </Button>
        </div>

        <div className="bg-white rounded-lg border border-soft-sand shadow-soft overflow-hidden">
          <table className="w-full">
            <thead className="bg-soft-sand">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-charcoal font-body">Code</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-charcoal font-body">Type</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-charcoal font-body">Value</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-charcoal font-body">Uses</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-charcoal font-body">Valid Until</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-charcoal font-body">Status</th>
                <th className="px-6 py-4 text-right text-sm font-semibold text-charcoal font-body">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-soft-sand">
              {promoCodes.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-stone-grey font-body">
                    No promo codes yet. Click &quot;Create Promo Code&quot; to add one.
                  </td>
                </tr>
              ) : (
                promoCodes.map((promo) => (
                  <tr key={promo._id} className="hover:bg-soft-sand/30 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <code className="font-mono font-bold text-charcoal bg-soft-sand px-3 py-1 rounded">{promo.code}</code>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-6 w-6"
                          onClick={() => copyToClipboard(promo.code)}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-stone-grey font-body capitalize">{promo.discount_type}</td>
                    <td className="px-6 py-4 font-medium text-charcoal font-body">
                      {promo.discount_type === 'percentage' ? `${promo.discount_value}%` : `$${promo.discount_value}`}
                    </td>
                    <td className="px-6 py-4 text-sm text-stone-grey font-body">
                      {promo.current_uses || 0} / {promo.max_uses || '∞'}
                    </td>
                    <td className="px-6 py-4 text-sm text-stone-grey font-body">
                      {formatDate(promo.valid_until)}
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => handleToggle(promo._id)}
                        className={`px-3 py-1 rounded text-sm font-medium ${
                          promo.is_active 
                            ? 'bg-muted-green/10 text-muted-green' 
                            : 'bg-stone-grey/10 text-stone-grey'
                        }`}
                      >
                        {promo.is_active ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="text-charcoal hover:bg-soft-sand"
                          onClick={() => handleOpenModal(promo)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="text-soft-terracotta hover:bg-soft-terracotta/10" 
                          onClick={() => handleDelete(promo._id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md shadow-xl">
            <div className="p-6 border-b border-soft-sand flex items-center justify-between">
              <h2 className="text-2xl font-bold text-charcoal font-heading">
                {editingPromo ? 'Edit Promo Code' : 'Create Promo Code'}
              </h2>
              <button onClick={handleCloseModal} className="text-stone-grey hover:text-charcoal">
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-charcoal mb-1 font-body">Code</label>
                <Input
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  placeholder="SAVE20"
                  required
                  className="uppercase"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-1 font-body">Type</label>
                  <select
                    value={formData.discount_type}
                    onChange={(e) => setFormData({ ...formData, discount_type: e.target.value })}
                    className="w-full px-3 py-2 border border-soft-sand rounded-md font-body"
                  >
                    <option value="percentage">Percentage</option>
                    <option value="fixed">Fixed Amount</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-1 font-body">
                    Value {formData.discount_type === 'percentage' ? '(%)' : '($)'}
                  </label>
                  <Input
                    type="number"
                    value={formData.discount_value}
                    onChange={(e) => setFormData({ ...formData, discount_value: parseFloat(e.target.value) || 0 })}
                    min="0"
                    step={formData.discount_type === 'percentage' ? '1' : '0.01'}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-1 font-body">Min Purchase ($)</label>
                  <Input
                    type="number"
                    value={formData.min_purchase}
                    onChange={(e) => setFormData({ ...formData, min_purchase: parseFloat(e.target.value) || 0 })}
                    min="0"
                    step="0.01"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-1 font-body">Max Uses</label>
                  <Input
                    type="number"
                    value={formData.max_uses}
                    onChange={(e) => setFormData({ ...formData, max_uses: parseInt(e.target.value) || 0 })}
                    min="0"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-1 font-body">Valid From</label>
                  <Input
                    type="date"
                    value={formData.valid_from}
                    onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-1 font-body">Valid Until</label>
                  <Input
                    type="date"
                    value={formData.valid_until}
                    onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={handleCloseModal} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 bg-muted-green hover:bg-muted-green/90 text-white">
                  <Save className="h-4 w-4 mr-2" />
                  {editingPromo ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminPromoCodes;
